/**
 * HRM gespreksagenda (batch-6 hrm) — Zod-schemas, DTO's en UI-labels.
 *
 * `HrGesprek` is het HR/TL-geïnitieerde plan-kanaal: HR plant een gesprek van
 * een configureerbaar type (`GesprekTypeConfig`), per deelnemer wordt
 * idempotent een interne werkbon op de BV van de medewerker gegenereerd
 * (`Werkbon.sourceHrGesprekId`, zelfde patroon als de cursussessie-generatie).
 *
 * Bewust gescheiden van het medewerker-aanvraagkanaal (`ContactRequest`,
 * Spec C — de batch-6-spec noemt nog het vervangen `GespreksAanvraag`); een
 * open contactverzoek kan via `bronContactRequestId` doorgelinkt worden.
 *
 * Privacy: een VERZUIMGESPREK is gevoelig. Het type is alleen zichtbaar voor
 * callers met `VERZUIM.canView` of `MEDEWERKERS.canEdit`; anderen zien de
 * neutrale titel "Gesprek" (redactie gebeurt server-side in de route). De
 * werkbon-/planbord-titel is bij VERZUIMGESPREK ALTIJD neutraal.
 */

import { z } from 'zod'
import { idParam } from './id-validators'
// batch-47 gespreksverslagen (spec 90) — statusverloop-instellingen per type.
import {
  GESPREKSVERSLAG_ONDERTEKEN_PLICHT,
  GESPREKSVERSLAG_TEKENVERKLARING,
  GESPREKSVERSLAG_TER_CONTROLE_MODUS,
  type GespreksverslagOndertekenPlicht,
  type GespreksverslagTekenverklaring,
  type GespreksverslagTerControleModus,
} from './gespreksverslag-status'

// ─── Enums & vaste keys ──────────────────────────────────────────────────────

export const HR_GESPREK_STATUSSEN = ['GEPLAND', 'VERZET', 'AFGEROND', 'GEANNULEERD'] as const
export type HrGesprekStatus = (typeof HR_GESPREK_STATUSSEN)[number]

export const HR_GESPREK_STATUS_LABELS: Record<HrGesprekStatus, string> = {
  GEPLAND:     'Gepland',
  VERZET:      'Verzet',
  AFGEROND:    'Afgerond',
  GEANNULEERD: 'Geannuleerd',
}

/**
 * Stabiele seed-keys van `GesprekTypeConfig`. De lijst is uitbreidbaar in de
 * DB; deze constanten bestaan voor de twee key-gebonden regels:
 *  - VERZUIMGESPREK → privacy-redactie (neutrale titel zonder VERZUIM.canView
 *    of MEDEWERKERS.canEdit) + altijd neutrale werkbon-titel.
 *  - OVERIG → `vrijeTitel` verplicht bij plannen.
 */
export const GESPREK_TYPE_KEY_VERZUIM = 'VERZUIMGESPREK'
export const GESPREK_TYPE_KEY_OVERIG = 'OVERIG'

/** Neutrale weergave-titel wanneer het type geredigeerd is. */
export const HR_GESPREK_NEUTRALE_TITEL = 'Gesprek'

// ─── Input schemas ───────────────────────────────────────────────────────────

/** "HH:mm" op het kwartier — werkbon-tijden vallen altijd op het kwartier. */
const kwartierTijd = z
  .string()
  .regex(/^([01]\d|2[0-3]):(00|15|30|45)$/, 'Tijd moet op het kwartier vallen (HH:00/15/30/45)')

export const CreateHrGesprekSchema = z.object({
  gesprekTypeConfigId: idParam('gesprekTypeConfigId'),
  /** Verplicht bij type-key OVERIG (route valideert tegen de config). */
  vrijeTitel: z.string().trim().min(1).max(200).optional(),
  /** NL-kalenderdag van het gesprek (yyyy-MM-dd). */
  datum: z.string().regex(/^\d{4}-\d{2}-\d{2}$/),
  /** Starttijd als NL-wandklok, op het kwartier. */
  startTijd: kwartierTijd,
  /** Duur in minuten; voorvult uit de type-config, aanpasbaar (kwartier-stappen). */
  duurMinuten: z.number().int().min(15).max(480).multipleOf(15),
  locatie: z.string().trim().max(300).optional(),
  /** Wie voert het gesprek — gekozen via EmployeePicker; route resolvet employee → userId. */
  gesprekspartnerEmployeeId: idParam('gesprekspartnerEmployeeId'),
  /**
   * De EERSTE is de medewerker over wie het gesprek gaat (wordt
   * `isOnderwerp`); de rest zijn overige aanwezigen die het gesprek ook in hun
   * planning krijgen.
   */
  deelnemerEmployeeIds: z.array(idParam('deelnemerEmployeeIds')).min(1).max(25),
  /** Interne notities; niet zichtbaar voor de medewerker. */
  notities: z.string().trim().max(4000).optional(),
  /** Optionele link naar het open contactverzoek (gaat op INGEPLAND). */
  bronContactRequestId: idParam('bronContactRequestId').optional(),
  /**
   * FO-387 — optionele koppeling aan een frequent-verzuim-signaal. Zet
   * `FrequentVerzuimSignaal.gesprekId` bij aanmaak zodat de inbox-reconciliatie het
   * open ACTIE-item sluit. Alleen relevant bij een VERZUIMGESPREK vanuit de
   * frequent-verzuim-deeplink.
   */
  signaalId: idParam('signaalId').optional(),
  /**
   * batch-39 gesprekken (FO-332) — "Nieuw gesprek + verslag" ZONDER agenda-item:
   * true = geen interne werkbon-generatie en geen "verslag maken"-taak/melding
   * (het verslag wordt direct aansluitend gemaakt). Default false = plannen
   * zoals altijd.
   */
  zonderAgendaItem: z.boolean().optional(),
})
export type CreateHrGesprekInput = z.infer<typeof CreateHrGesprekSchema>

/**
 * Verzetten/bijwerken. Datum/tijd/duur-wijziging verplaatst de gekoppelde
 * werkbonnen mee en zet de status op VERZET; deelnemer-wijzigingen genereren
 * of annuleren de bijbehorende werkbon.
 */
export const UpdateHrGesprekSchema = z.object({
  vrijeTitel: z.string().trim().min(1).max(200).nullable().optional(),
  datum: z.string().regex(/^\d{4}-\d{2}-\d{2}$/).optional(),
  startTijd: kwartierTijd.optional(),
  duurMinuten: z.number().int().min(15).max(480).multipleOf(15).optional(),
  locatie: z.string().trim().max(300).nullable().optional(),
  gesprekspartnerEmployeeId: idParam('gesprekspartnerEmployeeId').optional(),
  /** Zelfde conventie als bij aanmaken: de eerste is de medewerker (onderwerp). */
  deelnemerEmployeeIds: z.array(idParam('deelnemerEmployeeIds')).min(1).max(25).optional(),
  notities: z.string().trim().max(4000).nullable().optional(),
})
export type UpdateHrGesprekInput = z.infer<typeof UpdateHrGesprekSchema>

export const AfrondHrGesprekSchema = z.object({
  afrondNotitie: z.string().trim().max(4000).optional(),
})
export type AfrondHrGesprekInput = z.infer<typeof AfrondHrGesprekSchema>

export const AnnuleerHrGesprekSchema = z.object({
  reden: z.string().trim().max(1000).optional(),
})
export type AnnuleerHrGesprekInput = z.infer<typeof AnnuleerHrGesprekSchema>

export const ListHrGesprekkenQuerySchema = z.object({
  /** Periode-filter op geplandOp (ISO-datum, inclusief). */
  vanaf: z.string().regex(/^\d{4}-\d{2}-\d{2}$/).optional(),
  totEnMet: z.string().regex(/^\d{4}-\d{2}-\d{2}$/).optional(),
  employeeId: idParam('employeeId').optional(),
  status: z.enum(HR_GESPREK_STATUSSEN).optional(),
})
export type ListHrGesprekkenQuery = z.infer<typeof ListHrGesprekkenQuerySchema>

export const UpdateGesprekTypeConfigSchema = z.object({
  label: z.string().trim().min(1).max(100).optional(),
  duurMinuten: z.number().int().min(15).max(480).multipleOf(15).optional(),
  actief: z.boolean().optional(),
  volgorde: z.number().int().min(0).max(999).optional(),
  // FO-035 (batch-16) — gespreksverslag verplicht bij dit type.
  verslagVerplicht: z.boolean().optional(),
  // FO-309 P3 (batch-34) — verslagtermijn (1-30 dagen, spec §5) + gekoppelde
  // DOCX-template (huisstijl) per gesprekstype. `null` = koppeling wissen.
  verslagTermijnDagen: z.number().int().min(1).max(30).optional(),
  verslagTemplateId: idParam('verslagTemplateId').nullable().optional(),
  // batch-39 gesprekken (FO-332) — stilzwijgend akkoord (dagen na verzenden) +
  // beheerbare akkoord-bloktekst (null = STREEP-default) + cyclus-voorzet voor
  // "Volgende afspraak" (null = geen voorzet).
  stilzwijgendAkkoordDagen: z.number().int().min(1).max(60).optional(),
  stilzwijgendAkkoordTekst: z.string().trim().min(1).max(2000).nullable().optional(),
  cyclusVoorzet: z
    .array(z.object({ typeLabel: z.string().trim().min(1).max(200) }))
    .max(12)
    .nullable()
    .optional(),
  // ── batch-47 gespreksverslagen (spec 90 §3/§6/§7) ──
  // Interne controlestap (A1: default UIT), ondertekenplicht + toegestane
  // tekenverklaringen, en de termijnreeks van de lees-/ondertekentermijn.
  terControleModus: z.enum(GESPREKSVERSLAG_TER_CONTROLE_MODUS).optional(),
  ondertekenPlicht: z.enum(GESPREKSVERSLAG_ONDERTEKEN_PLICHT).optional(),
  tekenverklaringOpties: z.array(z.enum(GESPREKSVERSLAG_TEKENVERKLARING)).max(4).optional(),
  termijnHerinnering1Dagen: z.number().int().min(1).max(60).optional(),
  termijnHerinnering2Dagen: z.number().int().min(1).max(60).optional(),
  termijnEscalatieTlDagen: z.number().int().min(1).max(90).optional(),
  termijnEscalatieHrDagen: z.number().int().min(1).max(120).optional(),
  termijnVerlopenDagen: z.number().int().min(1).max(180).optional(),
})
  .superRefine((v, ctx) => {
    // De reeks moet oplopen: herinnering 1 < 2 < TL < HR < verlopen. Alleen
    // valideren wanneer de client de hele reeks meestuurt (partial PATCH).
    const reeks: Array<[string, number | undefined]> = [
      ['termijnHerinnering1Dagen', v.termijnHerinnering1Dagen],
      ['termijnHerinnering2Dagen', v.termijnHerinnering2Dagen],
      ['termijnEscalatieTlDagen', v.termijnEscalatieTlDagen],
      ['termijnEscalatieHrDagen', v.termijnEscalatieHrDagen],
      ['termijnVerlopenDagen', v.termijnVerlopenDagen],
    ]
    const gevuld = reeks.filter((r): r is [string, number] => typeof r[1] === 'number')
    for (let i = 1; i < gevuld.length; i += 1) {
      if (gevuld[i][1] <= gevuld[i - 1][1]) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: [gevuld[i][0]],
          message: 'Deze stap moet later liggen dan de vorige',
        })
      }
    }
  })
export type UpdateGesprekTypeConfigInput = z.infer<typeof UpdateGesprekTypeConfigSchema>

// ─── DTO's ───────────────────────────────────────────────────────────────────

export interface GesprekTypeConfigDTO {
  id: string
  key: string
  label: string
  duurMinuten: number
  actief: boolean
  volgorde: number
  /** FO-035 (batch-16) — is een gespreksverslag verplicht bij dit type? */
  verslagVerplicht: boolean
  /** FO-309 P3 — termijn (dagen na de gespreksdatum) voor het definitieve verslag. */
  verslagTermijnDagen: number
  /** FO-309 P3 — gekoppelde DOCX-verslag-template (huisstijl), of null (fallback-PDF). */
  verslagTemplateId: string | null
  verslagTemplateNaam: string | null
  // batch-39 gesprekken (FO-332) — stilzwijgend akkoord + cyclus-voorzet.
  /** Dagen na verzenden waarna stilzwijgend akkoord wordt geregistreerd. */
  stilzwijgendAkkoordDagen: number
  /** Beheerbare akkoord-bloktekst; null = STREEP-default (met {dagen}). */
  stilzwijgendAkkoordTekst: string | null
  /** Cyclus-voorzet voor "Volgende afspraak"; null/leeg = geen voorzet. */
  cyclusVoorzet: Array<{ typeLabel: string }> | null
  // ── batch-47 gespreksverslagen (spec 90 §3/§6/§7) ──
  /** Interne controlestap voor dit type (A1: default UIT). */
  terControleModus: GespreksverslagTerControleModus
  /** Wie moet(en) tekenen bij dit type (default GEEN = stilzwijgend akkoord). */
  ondertekenPlicht: GespreksverslagOndertekenPlicht
  /** Toegestane tekenverklaringen; leeg = alleen "gelezen en akkoord". */
  tekenverklaringOpties: GespreksverslagTekenverklaring[]
  /** Termijnreeks (dagen na verstrekken) van de lees-/ondertekentermijn. */
  termijnHerinnering1Dagen: number
  termijnHerinnering2Dagen: number
  termijnEscalatieTlDagen: number
  termijnEscalatieHrDagen: number
  termijnVerlopenDagen: number
}

export interface HrGesprekDeelnemerDTO {
  employeeId: string
  naam: string
  /** De medewerker over wie het gesprek gaat (staat altijd vooraan in `deelnemers`). */
  isOnderwerp: boolean
  /** BV waarop de interne werkbon voor deze deelnemer gepland is/wordt. */
  entityId: string | null
  entityNaam: string | null
  /** Gekoppelde interne werkbon (null = (nog) niet gegenereerd of geannuleerd). */
  werkbonId: string | null
  werkbonNumber: string | null
}

export interface HrGesprekDTO {
  id: string
  /**
   * Geredigeerd type: null wanneer de caller het type niet mag zien
   * (VERZUIMGESPREK zonder VERZUIM.canView/MEDEWERKERS.canEdit). `titel` is
   * dan de neutrale weergave-titel.
   */
  gesprekTypeConfigId: string | null
  typeKey: string | null
  typeLabel: string | null
  /** Weergave-titel: typeLabel of vrijeTitel, of "Gesprek" bij redactie. */
  titel: string
  vrijeTitel: string | null
  geplandOp: string // ISO UTC-instant
  duurMinuten: number
  locatie: string | null
  status: HrGesprekStatus
  gesprekspartnerUserId: string
  gesprekspartnerNaam: string | null
  /** Interne notities — alleen aanwezig voor MEDEWERKERS.canEdit-callers. */
  notities: string | null
  afrondNotitie: string | null
  afgerondOp: string | null
  bronContactRequestId: string | null
  deelnemers: HrGesprekDeelnemerDTO[]
  createdAt: string
  updatedAt: string
  /**
   * batch-39 gesprekken (FO-332) — samenvatting van het gekoppelde verslag
   * (voor de dossier-tab: status-badge + "Verslag maken"/"Verslag bekijken").
   */
  verslag: {
    id: string
    /** FO-378 — HERZIENING = "Nieuwe versie maken" gestart (terug in bewerking). */
    status: 'CONCEPT' | 'VRIJGEGEVEN' | 'HERZIENING'
    definitiefOp: string | null
    verzondenOp: string | null
    stilzwijgendAkkoordOp: string | null
    /** Nog-open actiepunten op dit verslag (voor de dossier-badge). */
    openActiepunten: number
  } | null
  /**
   * batch-41 gesprekken (FO-360 §3.2) — "los verslag": het (lichte)
   * drager-gesprek is zonder agenda-item/planning aangemaakt. Afgeleid (geen
   * model-wijziging): er is nooit een interne werkbon voor gegenereerd. Lijsten
   * tonen dan het label "los verslag" i.p.v. een geplande datum/tijd.
   */
  losVerslag: boolean
}
